import type { RoofModel, RoofFace, Obstruction } from '../types';

// ============================================================
// ROOF MODEL GENERATION
// MVP: Generates a simulated residential roof with 4 faces
// Future: Replace with OSM building footprints or AI CV detection
// ============================================================

export function generateRoofModel(): RoofModel {
  const roofWidth = 40;
  const roofDepth = 30;
  const roofHeight = 18;
  const pitchDeg = 25;
  const pitchRad = pitchDeg * Math.PI / 180;

  const faces: RoofFace[] = [
    {
      id: 'face_south',
      label: 'South',
      azimuth: 180,
      pitch: pitchDeg,
      vertices: [
        [-roofWidth / 2, -roofDepth / 2],
        [roofWidth / 2, -roofDepth / 2],
        [roofWidth / 2, 0],
        [-roofWidth / 2, 0],
      ],
      height: roofHeight,
      area_sqft: (roofWidth * (roofDepth / 2)) / Math.cos(pitchRad),
      solar_access: 88,
    },
    {
      id: 'face_north',
      label: 'North',
      azimuth: 0,
      pitch: pitchDeg,
      vertices: [
        [-roofWidth / 2, 0],
        [roofWidth / 2, 0],
        [roofWidth / 2, roofDepth / 2],
        [-roofWidth / 2, roofDepth / 2],
      ],
      height: roofHeight + Math.sin(pitchRad) * (roofDepth / 2),
      area_sqft: (roofWidth * (roofDepth / 2)) / Math.cos(pitchRad),
      solar_access: 52,
    },
    {
      id: 'face_east',
      label: 'East',
      azimuth: 90,
      pitch: pitchDeg,
      vertices: [
        [-roofWidth / 2, -roofDepth / 2],
        [-roofWidth / 2, 0],
        [-roofWidth / 2 + 8, 0],
        [-roofWidth / 2 + 8, -roofDepth / 2],
      ],
      height: roofHeight,
      area_sqft: (roofDepth * 8) / Math.cos(pitchRad),
      solar_access: 74,
    },
    {
      id: 'face_west',
      label: 'West',
      azimuth: 270,
      pitch: pitchDeg,
      vertices: [
        [roofWidth / 2 - 8, -roofDepth / 2],
        [roofWidth / 2, -roofDepth / 2],
        [roofWidth / 2, 0],
        [roofWidth / 2 - 8, 0],
      ],
      height: roofHeight,
      area_sqft: (roofDepth * 8) / Math.cos(pitchRad),
      solar_access: 76,
    },
  ];

  const obstructions: Obstruction[] = [
    { id: 'chimney', type: 'chimney', position: [5, -8], width: 3, height: 4 },
    { id: 'vent1', type: 'vent', position: [-8, -10], width: 1.5, height: 1.5 },
    { id: 'tree1', type: 'tree', position: [35, 20], width: 12, height: 25 },
  ];

  return { faces, obstructions, roofWidth, roofDepth, roofHeight, pitchDeg, source: 'simulated' as const };
}
